/**
 * Monitor/helpers — FEAT-MONITOR-001
 * Threshold policy + formatters shared across the Live Monitor tab. One place so a colour
 * or cutoff change is a single edit. Colour is never the only signal — every consumer pairs
 * it with a label/number.
 */

// Traffic-light tokens (Chakra colour scheme names + hex for inline styles).
export const TONE = {
  ok:    { scheme: "green",  fg: "#166534", bg: "#DCFCE7", dot: "#16A34A" },
  warn:  { scheme: "orange", fg: "#9A3412", bg: "#FFEDD5", dot: "#EA580C" },
  crit:  { scheme: "red",    fg: "#991B1B", bg: "#FEE2E2", dot: "#DC2626" },
  muted: { scheme: "gray",   fg: "#475569", bg: "#F1F5F9", dot: "#94A3B8" },
};

// Utilization % of a provisioned/committed ceiling.
export const classifyUtil = (pct) =>
  pct == null ? "muted" : pct < 70 ? "ok" : pct <= 90 ? "warn" : "crit";

// Seconds since last successful poll — page dims/labels as it ages.
export const classifyStale = (sec) =>
  sec == null ? "muted" : sec < 15 ? "ok" : sec <= 60 ? "warn" : "crit";

// A service ping status → tone.
export const classifyService = (status) =>
  status === "up" ? "ok" : status === "degraded" ? "warn" : status === "down" ? "crit" : "muted";

// Roll the loudest signal into the header pill. CRIT wins, then WARN, else OK.
export function overallHealth({ overview, staleSec }) {
  if (!overview) return "muted";
  const flags = [classifyStale(staleSec), classifyUtil(overview.capacity?.utilization_pct)];
  if (overview.capacity?.overcommitted) flags.push("crit");
  (overview.system || []).forEach((s) => flags.push(classifyService(s.status)));
  if (flags.includes("crit")) return "crit";
  if (flags.includes("warn")) return "warn";
  return "ok";
}

// ── formatters ────────────────────────────────────────────────────────────
export const fmtInt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));
export const fmtPct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);
export const fmtTps = (n) => (n == null ? "—" : `${Number(n).toLocaleString("en-US")} tps`);

export function fmtRelative(iso) {
  if (!iso) return "—";
  const sec = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  return `${Math.round(sec / 3600)}h ago`;
}

// Fallback bucket per window when the API rejects a combo with BAD_BUCKET (too few/many points).
export const DEFAULT_BUCKET_FOR = { "1h": "5m", "6h": "15m", "24h": "1h" };
export const WINDOW_OPTIONS = ["1h", "6h", "24h"];
export const BUCKET_HINT = { "1h": "1m", "6h": "5m", "24h": "15m" };

// Host-resource classifiers (mirror CLS Monitor cutoffs).
export const classifyCpu = (p) => (p == null ? "muted" : p < 60 ? "ok" : p <= 85 ? "warn" : "crit");
export const classifyRam = (p) => (p == null ? "muted" : p < 75 ? "ok" : p <= 90 ? "warn" : "crit");
export const classifyDisk = (p) => (p == null ? "muted" : p < 75 ? "ok" : p <= 90 ? "warn" : "crit");
export const classifyLag = (ms) => (ms == null ? "muted" : ms < 50 ? "ok" : ms <= 200 ? "warn" : "crit");
export const fmtDuration = (s) => {
  if (s == null) return "—";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
};
